import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api';
import MarkdownView from '@/components/MarkdownView';
import { Send, Loader2, Sparkles, MessageSquare, Plus, Trash2 } from 'lucide-react';

interface Conv {
  id: string;
  title: string | null;
  document_id: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

interface Turn {
  id: string;
  turn_index: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  citations?: any[] | null;
  model_used?: string | null;
  latency_ms?: number | null;
}

const API = import.meta.env.VITE_API_URL || 'http://localhost:8011';

interface Props {
  /** null = general (cross-doc), string = BAP-scoped */
  docId: string | null;
  /** Description shown in header */
  title?: string;
  subtitle?: string;
  /** Sticky sidebar list width — 0 = compact mode (no sidebar) */
  sidebarWidth?: number;
  /** Min height of chat panel */
  minHeight?: string;
}

export default function ChatPanel({
  docId,
  title = 'AI Asisten',
  subtitle,
  sidebarWidth = 260,
  minHeight = '70vh',
}: Props) {
  const qc = useQueryClient();
  const [convId, setConvId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [localTurns, setLocalTurns] = useState<Turn[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const scope = docId ? 'doc' : 'general';
  const qParam = docId ? `scope=doc&document_id=${docId}` : `scope=general`;

  const { data: convs } = useQuery<Conv[]>({
    queryKey: ['conversations', scope, docId || 'none'],
    queryFn: () => api(`/api/v1/chat/conversations?${qParam}`),
    refetchInterval: 10_000,
  });

  const { data: turns } = useQuery<Turn[]>({
    queryKey: ['turns', convId],
    queryFn: () => (convId ? api(`/api/v1/chat/conversations/${convId}/turns`) : Promise.resolve([])),
    enabled: !!convId,
  });

  useEffect(() => {
    setLocalTurns(turns || []);
  }, [turns]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localTurns]);

  // Reset selected conv when docId changes
  useEffect(() => {
    setConvId(null);
    setLocalTurns([]);
  }, [docId]);

  const newConv = async () => {
    const resp = await fetch(`${API}/api/v1/chat/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
      body: JSON.stringify({ document_id: docId, title: 'Konversasi baru' }),
    });
    const c = await resp.json();
    setConvId(c.id);
    setLocalTurns([]);
    qc.invalidateQueries({ queryKey: ['conversations', scope, docId || 'none'] });
  };

  const archive = async (id: string) => {
    await fetch(`${API}/api/v1/chat/conversations/${id}`, {
      method: 'DELETE',
      headers: { 'X-Dev-User': 'admin@netra.local' },
    });
    if (convId === id) {
      setConvId(null);
      setLocalTurns([]);
    }
    qc.invalidateQueries({ queryKey: ['conversations', scope, docId || 'none'] });
  };

  const send = async () => {
    if (!input.trim() || streaming) return;
    const msg = input;
    setInput('');
    setStreaming(true);

    const nextIdx = (localTurns.at(-1)?.turn_index ?? -1) + 1;
    const userTurn: Turn = { id: `tmp-u-${Date.now()}`, turn_index: nextIdx, role: 'user', content: msg };
    const assTurn: Turn = { id: `tmp-a-${Date.now()}`, turn_index: nextIdx + 1, role: 'assistant', content: '' };
    setLocalTurns((cur) => [...cur, userTurn, assTurn]);

    const targetConv = convId || '00000000-0000-0000-0000-000000000000';

    try {
      const resp = await fetch(`${API}/api/v1/chat/conversations/${targetConv}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
        body: JSON.stringify({ message: msg, document_id: docId, top_k: 6 }),
      });
      if (!resp.ok || !resp.body) throw new Error(await resp.text());

      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let assContent = '';
      let newConvId: string | null = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') continue;
          try {
            const ev = JSON.parse(raw);
            if (ev.type === 'conversation') newConvId = ev.data.id;
            else if (ev.type === 'token') {
              assContent += ev.data;
              setLocalTurns((cur) => {
                const next = [...cur];
                const idx = next.findIndex((t) => t.id === assTurn.id);
                if (idx >= 0) next[idx] = { ...next[idx], content: assContent };
                return next;
              });
            } else if (ev.type === 'done') {
              setLocalTurns((cur) => {
                const next = [...cur];
                const idx = next.findIndex((t) => t.id === assTurn.id);
                if (idx >= 0) next[idx] = { ...next[idx], content: assContent, citations: ev.data.citations, latency_ms: ev.data.latency_ms };
                return next;
              });
            } else if (ev.type === 'error') {
              assContent += `\n[ERROR] ${ev.data}`;
            }
          } catch { /* skip */ }
        }
      }
      if (newConvId && !convId) {
        setConvId(newConvId);
        qc.invalidateQueries({ queryKey: ['conversations', scope, docId || 'none'] });
      }
    } catch (e: any) {
      setLocalTurns((cur) => {
        const next = [...cur];
        const idx = next.findIndex((t) => t.id === assTurn.id);
        if (idx >= 0) next[idx] = { ...next[idx], content: `[ERROR] ${String(e)}` };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const showSidebar = sidebarWidth > 0;
  // Panel height — relative to viewport. Default leaves ~240px above for shell + dossier head.
  const panelHeight = minHeight || 'calc(100vh - 240px)';

  return (
    <div
      className="grid gap-6"
      style={{
        gridTemplateColumns: showSidebar ? `${sidebarWidth}px 1fr` : '1fr',
        height: panelHeight,
      }}
    >
      {showSidebar && (
        <aside className="border-r border-ink-line pr-4 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h2 className="font-display font-bold text-sm tracking-tight">Konversasi</h2>
            <button onClick={newConv} className="p-1.5 border border-ink-line hover:border-brass hover:text-brass transition" title="Konversasi baru">
              <Plus className="w-3 h-3" />
            </button>
          </div>

          <div className="space-y-1 overflow-y-auto flex-1 min-h-0 pr-1">
            {(convs || []).map((c) => (
              <div
                key={c.id}
                className={`group flex items-center gap-1 px-2 py-1.5 border cursor-pointer transition ${
                  convId === c.id ? 'border-brass bg-brass-soft/40' : 'border-ink-line hover:border-ink-2'
                }`}
                onClick={() => setConvId(c.id)}
              >
                <MessageSquare className="w-3 h-3 text-ink-3 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-ink truncate">{c.title || 'Tanpa judul'}</div>
                  <div className="font-mono text-[9px] text-ink-3">{new Date(c.updated_at).toLocaleDateString('id-ID')}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); archive(c.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:text-crimson transition"
                  title="Arsipkan"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {(!convs || convs.length === 0) && (
              <p className="font-mono text-[10px] text-ink-3 italic px-2">Belum ada konversasi.</p>
            )}
          </div>
        </aside>
      )}

      {/* Main chat panel — grid rows: header / scroll / form */}
      <main
        className="grid min-h-0 border border-ink-line bg-paper-2/20"
        style={{ gridTemplateRows: 'auto 1fr auto' }}
      >
        {/* HEADER — sticky atas */}
        {(title || subtitle) && (
          <div className="px-4 py-3 border-b border-ink-line bg-paper-2/40 backdrop-blur-sm">
            {title && (
              <h2 className="font-display font-bold text-[16px] tracking-tight flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brass" />
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mt-0.5">{subtitle}</p>
            )}
          </div>
        )}

        {/* MESSAGES — scrollable middle (WhatsApp-style bubbles) */}
        <div className="overflow-y-auto px-4 py-4 space-y-2 min-h-0">
          {localTurns.length === 0 && (
            <div className="text-center py-12 font-mono text-xs text-ink-3">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Mulai bertanya. Asisten akan jawab berbasis konteks{docId ? ' BAP ini' : ' seluruh data sistem'}.
            </div>
          )}

          {localTurns.map((t) => {
            const isUser = t.role === 'user';
            return (
              <div key={t.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[78%] px-3.5 py-2 shadow-sm relative ${
                    isUser
                      ? 'bg-bluestamp text-paper rounded-tl-lg rounded-tr-lg rounded-bl-lg'
                      : 'bg-paper border border-ink-line rounded-tl-lg rounded-tr-lg rounded-br-lg'
                  }`}
                >
                  {/* Role label kecil di atas */}
                  <div className={`font-mono text-[9px] uppercase tracking-widest font-bold mb-1 ${
                    isUser ? 'text-paper/70' : 'text-brass'
                  }`}>
                    {isUser ? 'Anda' : 'Asisten'}
                    {t.latency_ms != null && (
                      <span className="ml-2 normal-case font-normal tracking-normal opacity-70">
                        · {(t.latency_ms / 1000).toFixed(1)}s
                      </span>
                    )}
                  </div>
                  {isUser ? (
                    <div className="font-serif text-[14px] leading-snug whitespace-pre-wrap text-paper">{t.content}</div>
                  ) : (
                    <div className="text-[13px] leading-snug">
                      <MarkdownView>{t.content}</MarkdownView>
                      {streaming && t.id.startsWith('tmp-a-') && !t.content && (
                        <span className="inline-block w-2 h-3.5 bg-maroon align-text-bottom ml-0.5 animate-pulse" />
                      )}
                    </div>
                  )}
                  {t.citations && t.citations.length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {t.citations.slice(0, 8).map((c: any, i: number) => (
                        <span key={i} className="citation-chip">
                          [hal.{c.page_start}{c.page_end !== c.page_start ? `-${c.page_end}` : ''}]
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        {/* FORM — sticky bawah */}
        <div className="px-4 py-3 border-t border-ink-line bg-paper-2/40 backdrop-blur-sm flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder={docId ? 'Tanya tentang BAP ini...' : 'Tanya tentang data sistem...'}
            disabled={streaming}
            className="flex-1 px-3 py-2 border border-ink-line bg-paper text-ink font-mono text-[12px] focus:border-brass outline-none"
          />
          <button
            onClick={send}
            disabled={streaming || !input.trim()}
            className="px-4 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {streaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Kirim
          </button>
        </div>
      </main>
    </div>
  );
}
